import { Inline, Stack, Input as BaseInput, InputProps, Label } from '@front10/base-elements';
import { Icon, IconName } from '../../packages/base-elements/Icon/Icon';
import * as React from 'react';
import { forwardRef } from 'react';
import nanoid from 'nanoid';

interface Props extends InputProps {
  label: string;
  layout?: 'closed' | 'standard';
  rightIcon?: IconName;
  rightIconSize?: number;
  hasError?: boolean;
  errorMessage?: React.ReactNode;
  state?: 'focus';
  rightElement?: React.ReactNode;
  active?: boolean;
}

// TODO: Maybe move to theme
export const inputStyles = {
  layout: {
    variant: 'layout.layoutStyles'
  },
  layoutClosed: {
    variant: 'layout.layoutStylesClosed'
  },
  input: {
    variant: 'layout.inputStyles'
  }
} as const;

export const Input = forwardRef<HTMLInputElement, React.PropsWithChildren<Props>>(function Input(
  {
    label,
    layout = 'closed',
    rightIcon,
    rightIconSize,
    disabled,
    hasError,
    state,
    rightElement,
    errorMessage,
    ...props
  }: React.PropsWithChildren<Props>,
  ref
) {
  const divRef = React.createRef<HTMLDivElement>();
  const [id] = React.useState(nanoid());
  const labelElement = <Label htmlFor={id}>{label}</Label>;
  const input = (
    <Inline
      alignY="center"
      data-disabled={disabled ? '' : undefined}
      data-error={hasError ? '' : undefined}
      data-state={state}
      data-active={props.active ? '' : undefined}
      sx={layout === 'closed' ? inputStyles.layoutClosed : inputStyles.layout}
    >
      <Stack sx={{ flex: '1' }}>
        {layout === 'closed' ? labelElement : null}
        {props.children || (
          <div ref={divRef}>
            <BaseInput
              id={id}
              sx={inputStyles.input}
              disabled={disabled}
              aria-invalid={hasError}
              {...props}
              ref={ref}
            />
          </div>
        )}
      </Stack>

      {rightIcon && (
        <Icon icon={rightIcon ?? 'keyboard_arrow_down'} size={rightIconSize} fill="textSoft" />
      )}
      <div
        style={{ height: '100%' }}
        onClick={() => {
          const newDivRef: HTMLDivElement | null = divRef.current;
          if (newDivRef !== null) {
            const inputRef: HTMLInputElement = newDivRef?.lastChild as HTMLInputElement;
            inputRef.focus();
          }
        }}
      >
        {rightElement ?? null}
      </div>
    </Inline>
  );
  return (
    <Stack gap={1}>
      {layout === 'closed' ? null : labelElement}
      {input}
      {hasError ? errorMessage : null}
    </Stack>
  );
});
